'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PercentErrorCalculator() {
  const t = useTranslations("tools.percentErrorCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    observedValue: '',
    trueValue: '',
  });

  const [errors, setErrors] = useState({
    observedValue: '',
    trueValue: '',
  });

  const [results, setResults] = useState<{
    percentError: number;
    absoluteError: number;
    relativeError: number;
    observed: number;
    trueVal: number;
    accuracyLevel: string;
    accuracyClass: string;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    excellent: '#d1fae5',
    excellentText: '#065f46',
    good: '#dbeafe',
    goodText: '#1e40af',
    fair: '#fef3c7',
    fairText: '#92400e',
    poor: '#fee2e2',
    poorText: '#991b1b',
  };

  const validateInputs = () => {
    const newErrors = {
      observedValue: '',
      trueValue: '',
    };

    let isValid = true;

    if (inputs.observedValue === '' || isNaN(Number(inputs.observedValue))) {
      newErrors.observedValue = t('errorObservedInvalid');
      isValid = false;
    }

    if (inputs.trueValue === '' || isNaN(Number(inputs.trueValue))) {
      newErrors.trueValue = t('errorTrueInvalid');
      isValid = false;
    } else if (parseFloat(inputs.trueValue) === 0) {
      newErrors.trueValue = t('errorTrueZero');
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const getAccuracyLevel = (percentError: number) => {
    if (percentError < 1) return { level: t('accuracyExcellent'), class: 'excellent' };
    if (percentError < 5) return { level: t('accuracyGood'), class: 'good' };
    if (percentError < 10) return { level: t('accuracyFair'), class: 'fair' };
    return { level: t('accuracyPoor'), class: 'poor' };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    const observed = parseFloat(inputs.observedValue);
    const trueVal = parseFloat(inputs.trueValue);

    const absoluteError = Math.abs(observed - trueVal);
    const relativeError = absoluteError / Math.abs(trueVal);
    const percentError = relativeError * 100;

    const accuracy = getAccuracyLevel(percentError);

    setResults({
      percentError,
      absoluteError,
      relativeError,
      observed,
      trueVal,
      accuracyLevel: accuracy.level,
      accuracyClass: accuracy.class,
    });
  };

  const handleClear = () => {
    setInputs({
      observedValue: '',
      trueValue: '',
    });
    setErrors({
      observedValue: '',
      trueValue: '',
    });
    setResults(null);
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs({ ...inputs, [field]: value });
    if (errors[field as keyof typeof errors]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const getInterpretation = () => {
    if (!results) return '';

    const { percentError, observed, trueVal, accuracyLevel } = results;

    let interpretation = t('interpretationBase', {
      percentError: percentError.toFixed(4),
      accuracyLevel
    });

    if (percentError < 1) {
      interpretation += ' ' + t('interpretationExcellent');
    } else if (percentError < 5) {
      interpretation += ' ' + t('interpretationGood');
    } else if (percentError < 10) {
      interpretation += ' ' + t('interpretationFair');
    } else {
      interpretation += ' ' + t('interpretationPoor');
    }

    if (observed < trueVal) {
      interpretation += ' ' + t('interpretationNegative');
    } else if (observed > trueVal) {
      interpretation += ' ' + t('interpretationPositive');
    }

    return interpretation;
  };

  const getAccuracyBadgeColor = (accuracyClass: string) => {
    switch (accuracyClass) {
      case 'excellent':
        return { bg: colors.excellent, text: colors.excellentText };
      case 'good':
        return { bg: colors.good, text: colors.goodText };
      case 'fair':
        return { bg: colors.fair, text: colors.fairText };
      case 'poor':
        return { bg: colors.poor, text: colors.poorText };
      default:
        return { bg: colors.fair, text: colors.fairText };
    }
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '0.95rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.8125rem 1.125rem' },
    { padding: '0.875rem 1.25rem' }
  );

  const inputErrorStyle = {
    ...inputStyle,
    borderColor: '#ef4444',
  };

  const errorMessageStyle: React.CSSProperties = {
    color: '#ef4444',
    fontSize: '0.85rem',
    marginTop: '0.5rem',
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem' },
    { flexDirection: 'column' as const, marginTop: '1.5rem' },
    { flexDirection: 'row' as const, marginTop: '1.75rem' },
    { flexDirection: 'row' as const, marginTop: '2rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.9375rem 1.75rem', fontSize: '1.0625rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.9375rem 1.75rem', fontSize: '1.0625rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f1f5f9',
  };

  const resultLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.95rem',
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#1e293b' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
      marginTop: '1rem',
      fontFamily: "'Courier New', monospace",
      color: '#334155',
    },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '0.9375rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const interpretationStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const interpretationH4Style = getResponsiveStyle(
    { margin: '0 0 0.75rem 0', color: '#92400e' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const interpretationPStyle: React.CSSProperties = {
    margin: 0,
    color: '#475569',
    lineHeight: 1.6,
  };

  const accuracyBadgeStyle = (accuracyClass: string): React.CSSProperties => {
    const badgeColors = getAccuracyBadgeColor(accuracyClass);
    return {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '1rem',
      fontSize: '0.85rem',
      fontWeight: 600,
      marginLeft: '0.5rem',
      background: badgeColors.bg,
      color: badgeColors.text,
    };
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem',
  };

  const strongStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🎯 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="observed-value" style={labelStyle}>
                {t('labelObserved')}
              </label>
              <input
                type="number"
                id="observed-value"
                step="any"
                placeholder={t('placeholderObserved')}
                value={inputs.observedValue}
                onChange={(e) => handleInputChange('observedValue', e.target.value)}
                style={errors.observedValue ? inputErrorStyle : inputStyle}
              />
              {errors.observedValue && (
                <div style={errorMessageStyle}>{errors.observedValue}</div>
              )}
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="true-value" style={labelStyle}>
                {t('labelTrue')}
              </label>
              <input
                type="number"
                id="true-value"
                step="any"
                placeholder={t('placeholderTrue')}
                value={inputs.trueValue}
                onChange={(e) => handleInputChange('trueValue', e.target.value)}
                style={errors.trueValue ? inputErrorStyle : inputStyle}
              />
              {errors.trueValue && (
                <div style={errorMessageStyle}>{errors.trueValue}</div>
              )}
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultPercentError')}</span>
                  <span style={resultValueHighlightStyle}>{results.percentError.toFixed(4)}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultAbsoluteError')}</span>
                  <span style={resultValueStyle}>{results.absoluteError.toFixed(6)}</span>
                </div>
                <div style={{ ...resultItemStyle, borderBottom: 'none' }}>
                  <span style={resultLabelStyle}>{t('resultRelativeError')}</span>
                  <span style={resultValueStyle}>{results.relativeError.toFixed(6)}</span>
                </div>
              </div>

              <div style={formulaDisplayStyle}>
                {t('formulaLine1', {
                  observed: results.observed,
                  trueVal: results.trueVal
                })}<br />
                {t('formulaLine2', {
                  absoluteError: results.absoluteError.toFixed(6),
                  trueVal: Math.abs(results.trueVal)
                })}<br />
                {t('formulaLine3', {
                  percentError: results.percentError.toFixed(4)
                })}
              </div>

              <div style={interpretationStyle}>
                <h4 style={interpretationH4Style}>{t('interpretationTitle')}</h4>
                <p style={interpretationPStyle}>
                  {t('interpretationBase', {
                    percentError: results.percentError.toFixed(4),
                    accuracyLevel: ''
                  })}
                  <span style={accuracyBadgeStyle(results.accuracyClass)}>
                    {results.accuracyLevel}
                  </span>
                  . {getInterpretation().split('. ').slice(1).join('. ')}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
        <p style={paragraphStyle}>{t('insight5')}</p>
        <p style={paragraphStyle}>{t('insight6')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('formulaTitle')}
        </h3>
        <p style={paragraphStyle}>{t('formulaDescription')}</p>
        <div style={{ ...formulaDisplayStyle, marginBottom: '1.5rem' }}>
          {t('formulaText')}
        </div>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('applicationsTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('application1')}</li>
          <li style={listItemStyle}>{t('application2')}</li>
          <li style={listItemStyle}>{t('application3')}</li>
          <li style={listItemStyle}>{t('application4')}</li>
          <li style={listItemStyle}>{t('application5')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('bestPracticesTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq1Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq2Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq3Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq4Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq5Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://www.calculator.net/percent-error-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.indeed.com/career-advice/career-development/how-to-calculate-percent-error"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://statisticsbyjim.com/calculators/percent-error-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://study.com/academy/lesson/what-is-percentage-error-definition-example.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://statisticsbyjim.com/basics/percent-error/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}